import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface AuthRedirectProps {
  adminPath: string;
  organizationPath: string;
  userProfilePath: string;
  defaultPath: string;
}

/**
 * Composant responsable de la redirection après authentification
 * Redirige l'utilisateur vers la page appropriée en fonction de son rôle
 */
const AuthRedirect: React.FC<AuthRedirectProps> = ({
  adminPath,
  organizationPath,
  userProfilePath,
  defaultPath
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user, isNewUser } = useAuth();
  const [hasRedirected, setHasRedirected] = useState(false);
  
  // Vérifier si la redirection initiale a déjà été effectuée
  const [initialRedirectDone, setInitialRedirectDone] = useState(() => {
    return sessionStorage.getItem('initial_redirect_done') === 'true';
  });

  // Vérifier si l'utilisateur est une organisation
  const isOrganization = (user: any) => 
    user && (
      user.role === 'org_admin' || 
      user.userType === 'organization' || 
      user.profileType === 'organization'
    );

  // Vérifier si l'utilisateur est un administrateur
  const isAdmin = (user: any) => 
    user && (
      user.role === 'admin' || 
      user.role === 'ADMIN' || 
      user.email === 'adminetudown@admin.com'
    );

  useEffect(() => {
    // Si la redirection initiale est déjà effectuée, ne rien faire
    if (initialRedirectDone) {
      console.log('🔄 Redirection initiale déjà effectuée');
      return;
    }
    
    // Si les données sont en cours de chargement, attendre
    if (isLoading) {
      return;
    }
    
    // Si l'utilisateur n'est pas connecté, ne rien faire
    if (!isAuthenticated || !user) {
      return;
    }
    
    // Éviter les redirections multiples
    if (hasRedirected) {
      return;
    }
    
    // Ne pas rediriger pendant une redirection OAuth
    const isOAuthRedirect = sessionStorage.getItem('auth_redirect_triggered') === 'true';
    if (isOAuthRedirect) {
      return;
    }

    // Fonction pour traiter la redirection initiale
    const processRedirection = () => {
      let targetPath = defaultPath;
      
      // Déterminer la page cible selon le rôle de l'utilisateur
      if (isNewUser) {
        targetPath = userProfilePath;
      } else if (isAdmin(user)) {
        targetPath = adminPath;
      } else if (isOrganization(user)) {
        targetPath = organizationPath;
      }
      
      // Afficher un message de débogage
      console.log(`🎯 Redirection: ${user.email} (${user.role || 'rôle standard'}) vers ${targetPath}`);
      
      // Effectuer la redirection
      navigate(targetPath, { replace: true });
      setHasRedirected(true);
      
      // Marquer la redirection comme effectuée
      sessionStorage.setItem('initial_redirect_done', 'true');
      setInitialRedirectDone(true);
    };

    // Lancer la redirection avec un court délai
    const timer = setTimeout(() => {
      processRedirection();
    }, 300);
    
    return () => clearTimeout(timer);
  }, [
    isAuthenticated, 
    isLoading, 
    user, 
    isNewUser, 
    hasRedirected, 
    initialRedirectDone,
    navigate,
    adminPath,
    organizationPath,
    userProfilePath,
    defaultPath
  ]);

  // Ce composant ne rend rien visuellement
  return null;
};

export default AuthRedirect;
